import { useSyncExternalStore } from "react";

/**
 * ===========================================================================
 *  DEUX LANGUES : FRANCAIS ET DARIJA
 * ===========================================================================
 *
 *  POURQUOI
 *
 *  Les fournisseurs et le responsable commande travaillent sur ces ecrans
 *  toute la journee. Une partie d'entre eux ne lit pas le francais. Un bouton
 *  qu'on ne sait pas lire, on ne le touche pas — ou on le touche au hasard.
 *
 *  LE CHOIX DE LA CLE
 *
 *  La cle de traduction est LA PHRASE FRANCAISE elle-meme :
 *
 *      t("Marquer prêt")
 *
 *  et non un identifiant du genre `basket.markReady`. Deux raisons, et elles
 *  comptent plus que l'elegance :
 *
 *    1. Un mot oublie dans le dictionnaire s'affiche EN FRANCAIS, pas en
 *       `basket.markReady`. Le pire cas est un ecran a moitie traduit, jamais
 *       un ecran casse.
 *
 *    2. Le code reste lisible. On voit ce qui s'affiche en lisant la ligne,
 *       sans aller chercher ailleurs ce que veut dire la cle.
 *
 *  LA LANGUE
 *
 *  C'est de la DARIJA ecrite en lettres arabes — ce qu'on parle a Casa, pas
 *  l'arabe des journaux. « واجد » et non « جاهز ». Quelqu'un qui a peu ete a
 *  l'ecole lit sa langue parlee beaucoup plus vite que la langue ecrite.
 *
 *  OU EST STOCKE LE CHOIX
 *
 *  Dans le navigateur de la personne, et nulle part ailleurs. Chacun choisit
 *  sur son telephone, et son choix tient. Le faire dependre du compte
 *  obligerait l'admin a s'en occuper pour quelqu'un qui peut le faire
 *  lui-meme en un clic — et empecherait de verifier les deux versions depuis
 *  un seul compte.
 *
 *  PAS DE PROVIDER
 *
 *  On aurait pu passer par un contexte React. Il aurait fallu emballer la
 *  racine de l'application : un fichier de plus a modifier, sur un chemin que
 *  TOUS les ecrans traversent, y compris ceux qui n'ont rien a voir avec la
 *  traduction. Un magasin d'etat minuscule branche sur `useSyncExternalStore`
 *  fait la meme chose sans toucher a la racine.
 * ===========================================================================
 */

export type Langue = "fr" | "ar";

const CLE_STOCKAGE = "naturaLangue";

/**
 * On lit le navigateur une seule fois, et prudemment : en navigation privee
 * ou avec les donnees de site bloquees, `localStorage` LEVE au lieu de
 * renvoyer vide. Un ecran qui refuse de s'afficher parce qu'il n'a pas pu
 * lire une preference serait un bien mauvais echange.
 */
function lireLangue(): Langue {
  try {
    return localStorage.getItem(CLE_STOCKAGE) === "ar" ? "ar" : "fr";
  } catch {
    return "fr";
  }
}

let langueCourante: Langue = lireLangue();
const abonnes = new Set<() => void>();

function sabonner(f: () => void) {
  abonnes.add(f);
  return () => abonnes.delete(f);
}

function instantane(): Langue {
  return langueCourante;
}

/** Cote serveur il n'y a pas de navigateur : on rend le francais. */
function instantaneServeur(): Langue {
  return "fr";
}

export function definirLangue(l: Langue) {
  if (l === langueCourante) return;
  langueCourante = l;
  try {
    localStorage.setItem(CLE_STOCKAGE, l);
  } catch {
    // Le choix ne survivra pas au rechargement, mais l'ecran change tout de
    // suite — c'est le principal.
  }
  for (const f of abonnes) f();
}

/**
 * Le dictionnaire. A gauche la phrase francaise telle qu'elle est ecrite dans
 * le code, a droite la darija.
 *
 * Ce qui n'est PAS ici s'affiche en francais. C'est voulu : on peut traduire
 * au fur et a mesure sans jamais casser un ecran.
 */
const DARIJA: Record<string, string> = {
  // ---------------------------------------------------------------- general
  "Natura Beldi": "ناتورا بلدي",
  Dashboard: "الصفحة",
  Commandes: "الطلبات",
  Historique: "لي فات",
  Produits: "المنتوجات",
  Comptabilité: "الحساب",
  Horaires: "الأوقات",
  Déconnexion: "خروج",
  Déco: "خروج",
  Fournisseur: "المورّد",
  Collaborateur: "المسؤول",
  Magasin: "المحل",
  Date: "التاريخ",
  Détails: "التفاصيل",
  Annuler: "لغي",
  Valider: "صادق",
  Fermer: "سدّ",
  Chargement: "كيتحمّل",
  "Chargement…": "كيتحمّل…",
  "Traitement en cours...": "كيتسالا…",
  Ouvert: "محلول",
  Fermé: "مسدود",
  connecté: "مربوط",
  "hors ligne": "ماشي مربوط",
  "connexion…": "كيتربط…",

  // ------------------------------------------------------- ecran fournisseur
  "Gérez vos commandes en temps réel": "تبّع الطلبات ديالك فالوقت",
  "Vos produits à préparer": "المنتوجات لي خاصك تحضّر",
  "À préparer": "خاص يتحضّر",
  "À préparer maintenant": "حضّر دابا",
  "Prêts pour récupération": "واجدين للتسليم",
  "Aucune commande active": "ما كاين حتى طلب",
  "Les nouvelles commandes apparaîtront ici automatiquement":
    "الطلبات الجداد غايبانو هنا وحدهم",
  "Aucun fournisseur associé à votre compte":
    "ما كاين حتى مورّد مربوط بالحساب ديالك",
  "Détails commande": "تفاصيل الطلب",
  "Voir détails": "شوف التفاصيل",
  "Prêt depuis": "واجد من",
  "Code Retrait": "كود السحب",
  "sans code": "بلا كود",
  "aucune mesure": "ما تقاسش",

  // les etats d'un panier
  "En attente": "كيتسنّى",
  "en attente": "كيتسنّى",
  "En préparation": "كيتحضّر",
  "en cours": "كيتحضّر",
  Prêt: "واجد",
  prêt: "واجد",
  Prêts: "واجدين",
  "Prêtes": "واجدين",
  Récupéré: "تسلّم",
  récupéré: "تسلّم",
  Annulé: "تلغا",

  // les boutons du fournisseur — les plus importants de tout l'ecran
  COMMENCER: "بدا",
  "Commencer la préparation": "بدا التحضير",
  "MARQUER PRÊT": "واجد",
  "Marquer prêt": "علّم واجد",
  "Déclarer indisponible": "ما كاينش",
  "je ne l'ai pas": "ما عنديش",
  "Remboursements": "الإرجاعات",
  "Ce que Glovo nous a retenu, avec les photos": "شنو حبس لينا Glovo، بالتصاور",
  "Aucune photo pour cette retenue": "ما كاينة تصويرة لهاد الحبسة",
  "Certaines retenues n'ont pas de photo : elles restent contestables.": "بعض الحبسات ما فيهاش تصويرة — تقدر تعترض عليها.",
  "Historique des paniers": "تاريخ السلات",
  "Filtres": "الفلاتر",
  "Tous": "الكل",
  "Articles": "المنتوجات",
  "Créé le": "تصاوب ف",
  "Récupéré le": "تسالا ف",
  "Voir": "شوف",
  "Autres": "أخرى",
  "A tartiner": "لي كيتدهن",
  "Boissons Gazeuses": "مشروبات غازية",
  "Boissons Chaudes": "مشروبات سخونة",
  "Bonbons & gommes": "حلويات وعلك",
  "Boissons glacées": "مشروبات باردة",
  "Chats": "مشتقات القطط",
  "Chocolats, Nougats, Confiseries Chocolatées": "شوكولا ونوغا",
  "Conserves": "كونصيرف",
  "Couche & Serviette & hygiénique": "حفاضات وفوط",
  "Eaux Gazeuses": "ماء غازي",
  "Dattes & Fruits Secs": "تمر وفواكه جافة",
  "Epices et Assaisonnement": "عطرية وتوابل",
  "Eaux Plates": "ماء عادي",
  "Essentiels pour la maison": "ضروريات الدار",
  "Jus": "عصير",
  "Herbes aromatiques": "أعشاب",
  "Légumineuses.": "قطاني",
  "Lave-vaisselle": "غسول الماعون",
  "Lessive": "صابون الغسيل",
  "Les Huiles": "الزيوت",
  "Noix et graines": "لوز وزريعة",
  "Olives et légumes en conserve": "زيتون وخضرة فالكونصيرف",
  "Produits d'hygiène": "منتوجات النظافة",
  "Produits de boulangerie": "منتوجات الخبز",
  "Produits laitiers": "الحليب ومشتقاتو",
  "Prêt à manger": "واجد للماكلة",
  "Pâtes, riz et céréales": "معكرونة وروز وحبوب",
  "Pâtisserie": "حلويات",
  "Pâtisserie Traditionnelle": "حلويات تقليدية",
  "Pâtisserie Traditionnelle & Croissants": "حلويات تقليدية وكرواصون",
  "Sauces": "صلصات",
  "Viennoiseries & Brioches Fraiches": "كرواصون وبريوش",
  "pack et plateau.": "باك وطباسل",
  "Œufs": "البيض",
  "Boulangerie": "المخبزة",
  "Boulangerie Traditionelle": "خبز تقليدي",
  "Poissons": "الحوت",
  "Fruits de mer": "فواكه البحر",
  "Essentiels des fruits": "الفواكه",
  "Menthe": "النعناع",
  "Légumes Préparés": "خضرة محضرة",
  "Agrumes": "الحوامض",
  "Baies et petits fruits": "فواكه صغيرة",
  "Légumes bio": "خضرة بيو",
  "Salades": "الشلاضة",
  "Herbes": "أعشاب",
  "Boucherie": "الجزارة",
  "Viandes": "اللحوم",
  "Volaille": "الدجاج",
  "Commande": "طلب",
  "Rien sur 90 jours": "والو ف 90 يوم",
  "90 derniers jours": "آخر 90 يوم",
  "Mes Produits": "المنتوجات ديالي",
  "Rechercher un produit...": "قلب على منتوج...",
  "Tous statuts": "كلشي",
  "Actifs": "خدامين",
  "Inactifs": "واقفين",
  "Toutes": "الكل",
  "Actif": "خدام",
  "Inactif": "واقف",
  "Rupture": "ما بقاش",
  "Prix achat": "ثمن الشرا",
  "Image": "تصويرة",
  "Produit": "المنتوج",
  "Catégorie": "الصنف",
  "Unité": "الوحدة",
  "Statut": "الحالة",
  "Actions": "الخدمة",
  "En congé": "في العطلة",
  "En service": "خدام",
  "Service fermé": "الخدمة محلولة ماشي",
  "Tes articles ne sont pas en vente pendant ton congé.": "المنتوجات ديالك ما كيتباعوش فالعطلة.",
  "articles en vente sur Glovo": "منتوج كيتباع ف Glovo",
  "Tes articles ne sont pas en vente.": "المنتوجات ديالك ما كيتباعوش دابا.",
  "Je ferme mon service": "نسد الخدمة",
  "J'ouvre mon service": "نحل الخدمة",
  "Retire ton congé pour pouvoir rouvrir.": "نحي العطلة باش تقدر تحل.",
  "Prix d'achat": "ثمن الشرا",
  "Emballage": "التغليف",
  "Total": "المجموع",
  "Total prix d'achat": "مجموع ثمن الشرا",
  "Il sortira de ce qu'on te doit, et le magasin l'achètera à ta place.":
    "غايخرج من لي خاصك، والمحل غايشريه بلاصتك.",

  "C'est parti — préparation commencée.": "بدا التحضير.",
  "Déclaré indisponible — le magasin est prévenu.":
    "تسجّل: ما كاينش. المحل تبلّغ.",
  "Panier marqué comme prêt !": "الپانيي ولا واجد!",

  // --------------------------------------------- ecran responsable commande
  "À accepter": "للقبول",
  "En prép.": "كيتحضّر",
  "MARQUER PRÊTE": "واجدة",
  "Marquer prête": "علّم واجدة",
  "État des paniers": "حالة الپانيي",
  "Pas commencé": "مابداش",
  "À récupérer": "خاصو يتسلّم",
  "en préparation": "كيتحضّر",
  Préparation: "التحضير",
  min: "دقيقة",
  "Sac kraft": "ساك كرافت",
  "Sac tissu": "ساك دالتوب",
  "Nombre de sacs": "شحال من ساك",
  "Confirmer la livraison": "صادق التسليم",
  Accepter: "قبل",
  "Accepter la commande": "قبل الطلب",
  "ACCEPTER LA COMMANDE": "قبل الطلب",
  Acceptée: "تقبلات",
  Prête: "واجدة",
  Livrée: "تسلّمات",
  LIVRÉE: "تسلّمات",
  Annulée: "تلغات",
  "Annulée (Glovo)": "تلغات (Glovo)",
  "Prête depuis": "واجدة من",
  Récupérer: "تسلّم",
  "À récupérer chez": "خاصك تسلّم من",
  "MARQUER COMME PRÊTE": "علّم واجدة",
  "IMPRIMER LE TICKET": "طبع التيكي",
  "MARQUER COMME LIVRÉE": "علّم تسلّمات",
  "VALIDER LA LIVRAISON": "صادق التسليم",
  "Emballages utilisés": "الإمبالاج لي تستعمل",
  "Indique au moins un sac pour pouvoir valider.":
    "زيد على الأقل ساك واحد باش تقدر تصادق.",
  "Aucune commande": "ما كاين حتى طلب",
  "Aucune commande en attente": "ما كاين حتى طلب كيتسنّى",
  "Aucune commande en préparation": "ما كاين حتى طلب كيتحضّر",
  "Aucune commande prête": "ما كاين حتى طلب واجد",
  "Les commandes apparaîtront ici automatiquement":
    "الطلبات غايبانو هنا وحدهم",
  "Aucun store associé à votre compte": "ما كاين حتى محل مربوط بالحساب ديالك",
  "Contactez l'administrateur pour configurer votre accès.":
    "تواصل مع المسؤول باش يفتح ليك الدخول.",
  "Commande acceptée avec succès !": "الطلب تقبل!",
  "Commande marquée comme prête !": "الطلب ولا واجد!",
  "Panier récupéré !": "الپانيي تسلّم!",
  "Commande annulée": "الطلب تلغا",
  "Commande marquée comme livrée !": "الطلب تسلّم!",
  "Êtes-vous sûr de vouloir annuler cette commande ?":
    "واش متأكد بغيتي تلغي هاد الطلب؟",
  "Prép.": "كيتحضّر",

  // ----------------------------------- remboursements et ventes perdues
  "Ventes perdues": "البيع لي تفوّت",
  "Produits actifs": "المنتوجات الخدّامة",
  "sur 30 jours": "ف 30 يوم",
  "rien sur 30 jours": "والو ف 30 يوم",
  "aujourd'hui": "اليوم",
  "rien aujourd'hui": "والو اليوم",
  "commande aujourd'hui": "طلب اليوم",
  "commandes aujourd'hui": "طلبات اليوم",
  "À PRÉPARER EN MAGASIN": "خاص يتحضر فالمحل",
  "J'ai vu": "شفت",
  // ------------------------------------------- deux responsables (15/10/2026)
  "Je commence mon service": "نبدا الخدمة",
  "Pause déjeuner": "وقت الغدا",
  "repos aujourd'hui": "راحة اليوم",
  "est en repos aujourd'hui : toutes les commandes sont pour toi.": "فالراحة اليوم: كاع الطلبات ديالك.",
  "est en repos aujourd'hui : pas de pause possible, personne pour te relayer.": "فالراحة اليوم: ما كايناش الراحة ديال الغدا، حتى واحد ما غادي يعوضك.",
  "Partir en pause déjeuner 30 min ?": "نمشي نتغدّى 30 دقيقة؟",
  "commande(s) en cours passent à": "طلبات لي خدام عليهم غادي يمشيو عند",
  "Bon appétit ! Retour à": "بالصحة! الرجوع مع",
  "commande(s) restent à toi": "طلبات بقاو عندك",
  "Bon retour !": "مرحبا بيك!",
  "En pause jusqu'à": "فالراحة حتى",
  "en pause jusqu'à": "فالراحة حتى",
  "est en pause jusqu'à": "فالراحة حتى",
  "Les nouvelles commandes vont à l'autre responsable.": "الطلبات الجداد كيمشيو للمسؤول الآخر.",
  "Je reviens": "رجعت",
  "Personne d'autre n'est en service": "حتى واحد آخر ماشي فالخدمة",
  "ta pause sera possible à son retour.": "تقدر تمشي فاش يرجع.",
  "Pause possible quand l'autre responsable est en service.": "تقدر تمشي للراحة فاش يكون المسؤول الآخر فالخدمة.",
  "En service depuis": "فالخدمة من",
  "Terminer mon service": "نسالي الخدمة",
  "Pas en service": "ماشي فالخدمة",
  "en service": "فالخدمة",
  "Mes commandes": "الطلبات ديالي",
  "Les autres": "الآخرين",
  "À personne": "ماشي ديال حتى واحد",
  "Je la prends": "ناخدها",
  "Je m'en occupe": "أنا غادي نتكلف بها",
  Reprendre: "خودها",
  "prise en charge": "متكلف بها",
  "pas prise en charge depuis": "حتى واحد ماتكلف بها هادي",
  "Tant que tu n'es pas en service, les nouvelles commandes vont à": "حتى تبدا الخدمة، الطلبات الجداد كيمشيو عند",
  "Bon service !": "الله يعاون!",
  "Service terminé.": "سالات الخدمة.",
  "Commande reprise": "الطلب ولّى ديالك",
  "C'est noté": "تسجّل",
  "Reprendre la commande de": "تاخد الطلب ديال",
  "Aucune commande en cours chez": "حتى طلب ماخدام عند",
  "commande(s) reçue(s)": "طلبات وصلوك",
  "commande(s) restent à toi : aucun autre responsable en service.": "طلبات بقاو عندك: حتى مسؤول آخر ماشي فالخدمة.",
  "Terminer ton service ? Tes commandes en cours passent à l'autre responsable s'il est en service.": "واش نسالي الخدمة؟ الطلبات لي خدام عليهم غادي يمشيو للمسؤول الآخر إلا كان فالخدمة.",
  "Période du": "الفترة من",
  "au": "إلى",
  "articles sur 30 jours": "منتوج ف 30 يوم",
  "Ce qui a été retenu, avec la photo": "لي تقطع ليك، مع التصويرة",
  "Aucun remboursement": "ما كاين حتى إرجاع",
  "Rien ne t'a été retenu.": "ما تقطع ليك والو.",
  "Total retenu": "مجموع لي تقطع",
  Article: "المنتوج",
  "Photo du remboursement": "تصويرة الإرجاع",
  "Pas de photo pour ce remboursement": "ما كاينة تصويرة لهاد الإرجاع",
  // Retrait d'un article indisponible (21/10/2026)
  "Indisponible — demander le retrait": "ما كاينش — طلب نحيدوه",
  "Retirer cet article de la commande ? Le patron doit valider.":
    "نحيدو هاد المنتوج من الكوموند؟ خاص الباطرون يوافق.",
  "Oui, demander": "إيه، صيفط الطلب",
  Non: "لا",
  "Demande envoyée au patron": "الطلب مشا للباطرون",
  "Retrait demandé — en attente du patron": "طلبتي نحيدوه — كنتسناو الباطرون",
  "Retiré de la commande — ne pas préparer": "تحيّد من الكوموند — ما توجدوش",
  "Retrait validé — retire-le sur la tablette Glovo": "الباطرون وافق — حيدو من التابليت ديال Glovo",
  "Retrait refusé — à préparer": "الباطرون رفض — خاصك توجدو",
  // Retrait direct (02/11/2026)
  "Indisponible — retirer de la commande": "ما كاينش — حيدو من الكوموند",
  "Retirer cet article de la commande ? Il passera en ventes perdues.":
    "نحيدو هاد المنتوج من الكوموند؟ غادي يتحسب فالمبيعات الضايعة.",
  "Oui, retirer": "إيه، حيدو",
  "Article retiré — ventes perdues": "المنتوج تحيّد — المبيعات الضايعة",
  // Payer un article par la caisse (21/10/2026)
  "Payer par la caisse": "خلّص من الكاسة",
  "Total payé (DH)": "شحال خلّصتي فالمجموع (درهم)",
  "l'unité": "للوحدة",
  Enregistrer: "سجّل",
  "Payé par la caisse": "تخلّص من الكاسة",
  "Écris le total payé": "كتب شحال خلّصتي",
};

/**
 * Traduire une phrase.
 *
 * Absente du dictionnaire, elle revient telle quelle : un ecran a moitie
 * traduit se lit encore, un ecran plein de cles ne se lit plus.
 */
export function traduire(phrase: string, langue: Langue): string {
  if (langue === "fr") return phrase;
  return DARIJA[phrase] ?? phrase;
}

/**
 * Ce que les ecrans utilisent.
 *
 *     const { t, langue, rtl, basculer } = useLangue();
 *     <button>{t("Marquer prêt")}</button>
 */
export function useLangue() {
  const langue = useSyncExternalStore(sabonner, instantane, instantaneServeur);
  return {
    langue,
    /** L'arabe s'ecrit de droite a gauche : a poser sur `dir`. */
    rtl: langue === "ar",
    t: (phrase: string) => traduire(phrase, langue),
    definirLangue,
    basculer: () => definirLangue(langue === "fr" ? "ar" : "fr"),
  };
}
